import { useState, useCallback, useMemo, useEffect } from "react";
import { AttendanceService } from "@/services/Attendance/AttendanceService";
import { ThrowError } from "@/api/callApi";
import type {
  ISalaryPreviewUser,
  ISalaryPreviewData,
  ISalaryPreviewError,
} from "@/interface/attendance.interface";
import type { IPageInfo, IFilter } from "@/interface/common.interface";
import type { IBranchWiseSalaryHead } from "@/interface/salaryHead.interface";
import type { ISalaryUserBackup } from "@/components/common/SalaryConfigTable";
import type { ISalaryPreviewDraftResult } from "./useSalaryPreviewDraft";

interface UseSalaryPreviewDataParams {
  locationState: {
    userIds?: string[];
    startDate?: string;
    endDate?: string;
    siteId?: string;
  } | null;
  userSiteId?: string;
  initialDates: { startDate: string; endDate: string };
  draft: Pick<
    ISalaryPreviewDraftResult,
    "loadDraftFromStorage" | "applyDraftAndRecalculateUsers"
  >;
}

export interface ISalaryPreviewDataResult {
  isLoading: boolean;
  previewData: ISalaryPreviewData | null;
  setPreviewData: React.Dispatch<
    React.SetStateAction<ISalaryPreviewData | null>
  >;
  filter: IFilter;
  paginationData: IPageInfo;
  setPaginationData: React.Dispatch<React.SetStateAction<IPageInfo>>;
  allUsers: ISalaryPreviewUser[];
  errors: ISalaryPreviewError[];
  filteredUsers: ISalaryPreviewUser[];
  paginatedUsers: ISalaryPreviewUser[];
  backupUsersData: ISalaryUserBackup[];
  previewSalaryHeads: IBranchWiseSalaryHead[];
  handleFetchPreview: () => Promise<void>;
  handleDateRangeChange: (start: string, end: string) => void;
  handleSearchChange: (value: string) => void;
  handleChangePageSize: (value: number) => void;
}

export function useSalaryPreviewData({
  locationState,
  userSiteId,
  initialDates,
  draft,
}: UseSalaryPreviewDataParams): ISalaryPreviewDataResult {
  // Destructure stable function refs from draft to avoid object identity issues in useCallback deps
  const { loadDraftFromStorage, applyDraftAndRecalculateUsers } = draft;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<ISalaryPreviewData | null>(
    null,
  );
  const [filter, setFilter] = useState<IFilter>({
    search: "",
    startDate: initialDates.startDate,
    endDate: initialDates.endDate,
  });
  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // --- Filter & Pagination Handlers ---
  const handleDateRangeChange = useCallback((start: string, end: string) => {
    setFilter((prev) => ({ ...prev, startDate: start, endDate: end }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleChangePageSize = useCallback((value: number) => {
    setPaginationData((prev) => ({
      ...prev,
      pageSize: value,
      currentPage: 1,
    }));
  }, []);

  // --- API Fetch ---
  const handleFetchPreview = useCallback(async () => {
    setIsLoading(true);
    try {
      let targetUserIds = locationState?.userIds || [];

      // Fallback: If userIds is empty (direct page access or refresh), resolve from attendance summary
      if (!targetUserIds || targetUserIds.length === 0) {
        const currentSiteId = locationState?.siteId || userSiteId || "";
        const siteParam = currentSiteId ? `&siteId=${currentSiteId}` : "";
        const summaryParams = `type=monthly&startDate=${
          filter.startDate || initialDates.startDate
        }&endDate=${
          filter.endDate || initialDates.endDate
        }&pageNumber=1&pageSize=1000${siteParam}`;

        const summaryRes =
          await AttendanceService.getDailyTeamAttendance(summaryParams);
        if (summaryRes?.status && summaryRes?.data?.users) {
          targetUserIds = summaryRes.data.users
            .map((u) => u.user?.id)
            .filter(Boolean) as string[];
        }
      }

      const payload = {
        userIds: targetUserIds,
        startDate: filter.startDate || initialDates.startDate,
        endDate: filter.endDate || initialDates.endDate,
        siteId: locationState?.siteId || userSiteId || "",
      };

      const response = await AttendanceService.previewMonthlySalary(payload);
      const rawData = response?.data as ISalaryPreviewData;
      const savedEdits = loadDraftFromStorage();

      if (rawData?.users) {
        // Clone to avoid mutating the original API response object
        const data: ISalaryPreviewData = { ...rawData };
        if (savedEdits && Object.keys(savedEdits).length > 0) {
          data.users = applyDraftAndRecalculateUsers(rawData.users, savedEdits);
        }
        setPreviewData(data);
      } else {
        setPreviewData(rawData || null);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    locationState?.userIds,
    locationState?.siteId,
    filter.startDate,
    filter.endDate,
    initialDates.startDate,
    initialDates.endDate,
    userSiteId,
    loadDraftFromStorage,
    applyDraftAndRecalculateUsers,
  ]);

  useEffect(() => {
    handleFetchPreview();
  }, [handleFetchPreview]);

  // --- Derived Data ---
  const allUsers: ISalaryPreviewUser[] = previewData?.users || [];
  const errors = previewData?.errors || [];

  const filteredUsers = useMemo(() => {
    if (!filter.search) return allUsers;
    const term = filter.search.toLowerCase();
    return allUsers.filter((u) => {
      const name = (u.user?.name || "").toLowerCase();
      const code = (u.user?.employeeCode || "").toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [allUsers, filter.search]);

  const paginatedUsers = useMemo(() => {
    const page = paginationData.currentPage || 1;
    const limit = paginationData.pageSize || 10;
    const start = (page - 1) * limit;
    return filteredUsers.slice(start, start + limit);
  }, [filteredUsers, paginationData.currentPage, paginationData.pageSize]);

  // Transform paginated users → ISalaryUserBackup[] for SalaryConfigTable
  const backupUsersData: ISalaryUserBackup[] = useMemo(() => {
    return paginatedUsers?.map((u) => ({
      id: u.user?.id || "",
      name: u.user?.name || "—",
      employeeCode: u.user?.employeeCode || "—",
      salaries: u.userSalary?.map((sal) => {
        const headId = sal.branchSalaryHeadId || sal.id || sal.salaryName || "";
        const headName = sal.salaryName || sal.name || "—";
        return {
          id: headId,
          amount: sal.amount ?? 0,
          salaryName: headName,
          userId: u.user?.id || "",
          branchSalaryHeadId: headId,
          branchSalaryHead: {
            id: headId,
            salaryName: headName,
            salaryHead: {
              id: headId,
              name: headName,
              type: sal.type || "earning",
            },
          },
        };
      }),
    }));
  }, [paginatedUsers]);

  // Extract unique salary heads across all users for SalaryConfigTable header.
  // Two-pass deduplication: by headId AND by normalized salaryName so that
  // "Net Salary" and "Net Pay" (semantically identical) don't create two columns.
  const previewSalaryHeads: IBranchWiseSalaryHead[] = useMemo(() => {
    const map = new Map<string, IBranchWiseSalaryHead>();
    const seenNames = new Set<string>(); // prevents duplicate columns by display name

    allUsers?.forEach((u) => {
      u.userSalary?.forEach((sal) => {
        const headId = sal.branchSalaryHeadId || sal.id || sal.salaryName;
        const headName = sal.salaryName || sal.name || "Unknown";
        const normalizedName = headName.toLowerCase().trim();

        // Skip if already added by ID or by display name
        if (!headId || map.has(headId) || seenNames.has(normalizedName)) return;

        map?.set(headId, {
          id: headId,
          isFixed: false,
          amount: sal.amount ?? 0,
          rate: null,
          maxAmount: null,
          employeeRate: null,
          employerRate: null,
          salaryName: headName,
          branchId: "",
          salaryHeadId: headId,
          createdAt: "",
          updatedAt: "",
          deletedAt: null,
          salaryHead: {
            id: headId,
            name: headName,
            type: sal.type || "earning",
            amountType: (sal.amountType as any) || "value",
            isTaxable: sal.isTaxable ?? false,
            taxExempted: sal.taxExempted ?? false,
            frequency: sal.frequency ?? null,
            payableIn: sal.payableIn ?? null,
            fixVariable: sal.fixVariable ?? null,
            isActive: sal.isActive ?? true,
            partOfForm16: sal.partOfForm16 ?? false,
            maxCap: sal.maxCap,
            wantToShowInOfferLetter: sal.wantToShowInOfferLetter ?? false,
            isBase: sal.isBase ?? false,
            takeInput: sal.takeInput ?? false,
            canAppearInBasedOn: sal.canAppearInBasedOn ?? false,
            fromEmployeer: sal.fromEmployeer ?? false,
            createdAt: "",
            updatedAt: "",
            deletedAt: null,
            branchSalaryHeadId: headId,
            amount: sal.amount ?? 0,
            salaryName: headName,
            userId: sal.userId,
          },
        });
        seenNames.add(normalizedName);
      });
    });
    return Array.from(map.values());
  }, [allUsers]);

  // Keep pagination total in sync with filtered list
  useEffect(() => {
    const total = filteredUsers.length;
    const limit = paginationData.pageSize || 10;
    const totalPages = Math.ceil(total / limit) || 1;
    setPaginationData((prev) => ({ ...prev, total, totalPages }));
  }, [filteredUsers.length, paginationData.pageSize]);

  return {
    isLoading,
    previewData,
    setPreviewData,
    filter,
    paginationData,
    setPaginationData,
    allUsers,
    errors,
    filteredUsers,
    paginatedUsers,
    backupUsersData,
    previewSalaryHeads,
    handleFetchPreview,
    handleDateRangeChange,
    handleSearchChange,
    handleChangePageSize,
  };
}
